// THE BLACK COURT on screen (sim/bosses/court.ts). Sixteen pieces from six GLBs
// (Assets/bosses/chess_*/, packed into public/assets/run/ by pack_glb.mjs), each drawn as ONE
// InstancedMesh per model part, so the whole court costs ~55 draws however many stand; the board is
// one more (64 instanced tiles). Wired by part NAME (body, base, head, turret, crown, arm_*, leg_*,
// back_hatch, wp_back_core, fx_core), so a remodelled GLB with the same names drops straight in.
//
// What reads, from across the arena:
//   - the opener: the checker board erupts out of the grass tile by tile, then the court rises
//     onto its squares a rank at a time, the King last
//   - the court clock: on every tock the pieces step to new squares together (knights hop), then hold
//   - the King: a purple riveted chess-king robot (bell foot, egg torso, sphere head with one cyan slit,
//     spiked gold crown); the GOLD lens in his rear door's porthole throbs (the only way in), flaring
//     when he scuttles with his back turned. Textured models: one baked `paint` atlas + glow_* emissives
//   - tells: a rook's charge lane, a bishop's X of diagonals, a knight's landing ring, the queen's
//     eight-pointed star, the King's whip ring. Each fills toward impact, then the lines burn
//   - a fallen piece's home square glows ivory while it re-forms, and it rises out of the board

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { BossBeat, BossKind } from '../../../../sim/bosses/types';
import {
  ATTACK_NAMES, Atk, CPart, CourtBit, CourtEv, CourtNet, DIRS8, Pc, PIECE_NAMES, SQ, Stage,
  boardDir, cellWorld, isCourt, pieceOf, pieceRaycast,
} from '../../../../sim/bosses/court';
import { EFlag } from '../../../../sim/protocol';
import { withHaze } from '../../materials';
import type { HudBoss, HudObjective } from '../../contracts';
import { DANGER, Decal, groundDecal } from './decals';
import type { BossAim, BossClient, BossClientFactory, BossCtx, BossFrame } from './types';

const BASE = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';
const FILES = ['chess_king', 'chess_queen', 'chess_rook', 'chess_bishop', 'chess_knight', 'chess_pawn'];
/** instances per piece type (the court's count, plus headroom for a body dying as its replacement rises) */
const MAX = [1, 2, 3, 3, 3, 10];
const NODES = ['body', 'base', 'head', 'turret', 'crown', 'arm_L', 'arm_R', 'leg_L', 'leg_R', 'leg_B', 'back_hatch', 'wp_back_core', 'fx_core'];
const LIGHT = new THREE.Color(0.66, 0.63, 0.57), DARK = new THREE.Color(0.13, 0.13, 0.16);
const IVORY = new THREE.Color(0.95, 0.88, 0.62);
const TILE_H = 0.5, TILE_UP = 0.07;
const RISE_DROP = 2.6;
const PART_LABEL: Record<number, string> = {
  [CPart.Chassis]: 'CHASSIS · AP5', [CPart.Crown]: 'CROWN · AP5', [CPart.Core]: 'GOLD CORE · CRIT',
  [CPart.Body]: 'PLATE', [CPart.Head]: 'HEAD', [CPart.Weak]: 'FACE · CRIT', [CPart.Legs]: 'LEGS',
};

interface Kind {
  template: THREE.Object3D;
  nodes: Record<string, THREE.Object3D | null>;
  rest: Map<THREE.Object3D, { p: THREE.Vector3; r: THREE.Euler }>;
  parts: { src: THREE.Mesh; inst: THREE.InstancedMesh }[];
  n: number;
}
interface Unit { x: number; z: number; walk: number; seen: number }

class CourtClient implements BossClient {
  readonly kind = BossKind.BlackCourt;
  readonly title = 'THE BLACK COURT';
  readonly sub = 'CHECKMATE IS THE ONLY EXIT';
  private kinds: (Kind | null)[] = [null, null, null, null, null, null];
  private tiles: THREE.InstancedMesh;
  private tileGeo: THREE.BoxGeometry;
  private tileMat: THREE.MeshStandardMaterial;
  private boardKey = '';
  private glow: THREE.Sprite;
  private lanes: Decal[] = [];
  private beams: THREE.Mesh[] = [];
  private beamMat: THREE.MeshBasicMaterial;
  private beamGeo: THREE.BoxGeometry;
  private strike: Decal;
  /** dark pools under the danger decals: additive orange alone washes out on golden grass (G2) */
  private under: THREE.Mesh[] = [];
  private underDisc: THREE.Mesh;
  private reform: Decal[] = [];
  private units = new Map<number, Unit>();
  private col = new THREE.Color();
  private m4 = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private v1 = new THREE.Vector3();
  private v2 = new THREE.Vector3();
  private s1 = new THREE.Vector3(1, 1, 1);
  private o2 = { x: 0, z: 0 };
  private part = { t: -1, vol: null as null | { part: CPart; av: number; weak: boolean } };
  private disposables: { dispose(): void }[] = [];
  private burnT = -1;
  private time = 0;

  constructor(private ctx: BossCtx) {
    const loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
    Promise.all(FILES.map((f) => loader.loadAsync(BASE + 'assets/run/' + f + '.glb')))
      .then((gs) => gs.forEach((g, i) => this.onModel(i, g.scene)))
      .catch((e) => console.warn('[court] models failed', e));
    // the board: 64 stone tiles in one draw
    this.tileGeo = new THREE.BoxGeometry(SQ * 0.97, TILE_H, SQ * 0.97);
    this.tileMat = withHaze(new THREE.MeshStandardMaterial({ roughness: 0.82, metalness: 0.08 }), 0.25);
    this.tiles = new THREE.InstancedMesh(this.tileGeo, this.tileMat, 64);
    this.tiles.receiveShadow = true; this.tiles.castShadow = false; this.tiles.frustumCulled = false; this.tiles.visible = false;
    for (let f = 0; f < 8; f++) for (let r = 0; r < 8; r++) this.tiles.setColorAt(f * 8 + r, (f + r) % 2 === 0 ? DARK : LIGHT);
    ctx.scene.add(this.tiles);
    this.disposables.push(this.tileGeo, this.tileMat);
    // the King's gold core
    const tex = glowTex();
    const gm = new THREE.SpriteMaterial({ map: tex, color: 0xffa634, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, opacity: 0 });
    this.glow = new THREE.Sprite(gm); this.glow.renderOrder = 46; this.glow.visible = false;
    ctx.scene.add(this.glow);
    this.disposables.push(tex, gm);
    // telegraphs: eight lanes (the queen's star), one strike disc, and the re-forming squares
    for (let i = 0; i < 8; i++) this.lanes.push(groundDecal(ctx.scene, ctx.time, 0, DANGER));
    this.strike = groundDecal(ctx.scene, ctx.time, 3, DANGER);
    for (let i = 0; i < 6; i++) this.reform.push(groundDecal(ctx.scene, ctx.time, 3, IVORY));
    const um = new THREE.MeshBasicMaterial({ color: 0x140806, transparent: true, opacity: 0.34, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -3 });
    const ug = new THREE.PlaneGeometry(1, 1); ug.rotateX(-Math.PI / 2);
    const dg = new THREE.CircleGeometry(0.5, 40); dg.rotateX(-Math.PI / 2);
    for (let i = 0; i < 8; i++) { const m = new THREE.Mesh(ug, um); m.visible = false; m.renderOrder = 29; m.frustumCulled = false; ctx.scene.add(m); this.under.push(m); }
    this.underDisc = new THREE.Mesh(dg, um); this.underDisc.visible = false; this.underDisc.renderOrder = 29; this.underDisc.frustumCulled = false;
    ctx.scene.add(this.underDisc);
    this.disposables.push(um, ug, dg);
    this.beamGeo = new THREE.BoxGeometry(1, 1, 1);
    this.beamMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(1.6, 0.55, 0.2), transparent: true, opacity: 0.8, blending: THREE.AdditiveBlending, depthWrite: false });
    for (let i = 0; i < 8; i++) {
      const b = new THREE.Mesh(this.beamGeo, this.beamMat);
      b.visible = false; b.renderOrder = 31; b.frustumCulled = false;
      ctx.scene.add(b); this.beams.push(b);
    }
    this.disposables.push(this.beamGeo, this.beamMat);
  }

  private onModel(pc: number, root: THREE.Object3D) {
    const mats = new Map<THREE.Material, THREE.Material>();
    const prep = (m: THREE.Material) => {
      let o = mats.get(m);
      if (!o) { o = withHaze(m, m.name === 'paint' ? 0.35 : 0.2); mats.set(m, o); this.disposables.push(o); }
      return o;
    };
    const k: Kind = { template: root, nodes: {}, rest: new Map(), parts: [], n: 0 };
    root.traverse((o) => {
      if ((o as THREE.Mesh).isMesh) {
        const m = o as THREE.Mesh;
        const inst = new THREE.InstancedMesh(m.geometry, prep(m.material as THREE.Material), MAX[pc]);
        inst.castShadow = true; inst.receiveShadow = true; inst.frustumCulled = false; inst.count = 0;
        inst.name = 'court_' + FILES[pc] + '_' + m.name;
        for (let i = 0; i < MAX[pc]; i++) inst.setColorAt(i, this.col.setRGB(1, 1, 1));
        this.ctx.scene.add(inst);
        k.parts.push({ src: m, inst });
        this.disposables.push(m.geometry);
      }
      if (o !== root) k.rest.set(o, { p: o.position.clone(), r: o.rotation.clone() });
    });
    for (const n of NODES) k.nodes[n] = root.getObjectByName(n) ?? null;
    this.kinds[pc] = k;
    this.ctx.prewarm?.(k.parts.map((p) => p.inst));
  }

  update(f: BossFrame | null, time: number, dt: number) {
    this.time = time;
    for (const l of this.lanes) l.mesh.visible = false;
    for (const u of this.under) u.visible = false;
    this.underDisc.visible = false;
    for (const b of this.beams) b.visible = false;
    for (const r of this.reform) r.mesh.visible = false;
    this.strike.mesh.visible = false;
    if (!f) { this.hide(); return; }
    const net = f.net as CourtNet;
    this.board(f, net);
    this.poseUnits(f, net, time, dt);
    this.telegraphs(f, net, time);
    // the re-forming squares (G5): the home square glows ivory, brighter as the piece nears
    for (let i = 0, j = 0; i < net.pending.length && j < this.reform.length; i++) {
      const pd = net.pending[i];
      if (pd.k < 0.55) continue;
      const d = this.reform[j++];
      cellWorld(net, pd.f, pd.r, this.o2);
      d.mesh.visible = true;
      d.mesh.position.set(this.o2.x, f.ground(this.o2.x, this.o2.z) + TILE_UP + 0.04, this.o2.z);
      d.mesh.scale.set(SQ * 0.9, 1, SQ * 0.9);
      d.u.uK.value = (pd.k - 0.55) / 0.45;
    }
  }

  private hide() {
    for (const k of this.kinds) if (k) for (const p of k.parts) p.inst.count = 0;
    this.tiles.visible = false;
    this.glow.visible = false;
    this.units.clear();
    this.boardKey = '';
  }

  /** lay the 64 tiles on the board's squares, erupting out of the ground during the opener */
  private board(f: BossFrame, net: CourtNet) {
    const rising = net.rise < 1;
    const key = `${net.bx.toFixed(2)},${net.bz.toFixed(2)},${net.byaw.toFixed(3)}`;
    if (!rising && key === this.boardKey) return;
    this.boardKey = rising ? '' : key;
    this.tiles.visible = true;
    this.q.setFromAxisAngle(this.v1.set(0, 1, 0), net.byaw);
    for (let fi = 0; fi < 8; fi++) for (let r = 0; r < 8; r++) {
      cellWorld(net, fi, r, this.o2);
      // the eruption ripples out from the centre (Godot: ripple 0.08 s per square of distance)
      const delay = Math.hypot(fi - 3.5, r - 3.5) * 0.06;
      const k = Math.min(1, Math.max(0, (net.rise - delay) / Math.max(0.2, 1 - delay)));
      const ease = 1 + 2.2 * Math.pow(k - 1, 3) + 1.2 * Math.pow(k - 1, 2); // back-out
      const top = f.ground(this.o2.x, this.o2.z) + TILE_UP;
      const y = top - TILE_H / 2 - (1 - ease) * (TILE_H + 1.2);
      this.m4.compose(this.v2.set(this.o2.x, y, this.o2.z), this.q, this.s1);
      this.tiles.setMatrixAt(fi * 8 + r, this.m4);
    }
    this.tiles.instanceMatrix.needsUpdate = true;
    if (this.tiles.instanceColor) this.tiles.instanceColor.needsUpdate = true;
  }

  private poseUnits(f: BossFrame, net: CourtNet, time: number, dt: number) {
    for (const k of this.kinds) if (k) k.n = 0;
    let coreSeen = false;
    for (let i = 0; i < f.n; i++) {
      const e = f.units[i];
      if (!isCourt(e.type)) continue;
      const pc = pieceOf(e.type);
      const K = this.kinds[pc];
      if (!K || K.n >= MAX[pc]) continue;
      let u = this.units.get(e.id);
      if (!u) { u = { x: e.x, z: e.z, walk: (e.id * 1.7) % 6, seen: time }; this.units.set(e.id, u); }
      const spd = Math.hypot(e.x - u.x, e.z - u.z) / Math.max(1e-3, dt);
      u.x = e.x; u.z = e.z; u.seen = time;
      u.walk += dt * Math.min(12, spd * 3.2);
      const sw = Math.min(1, spd / 2);
      const T = K.template, N = K.nodes;
      // rising out of the board (spawn), hopping, bobbing
      let y = e.y + TILE_UP;
      if (e.st === 5) { const k = Math.min(1, e.prog); y -= RISE_DROP * (1 - k * k * (3 - 2 * k)); }
      if (pc === Pc.Pawn) y += Math.abs(Math.sin(u.walk)) * 0.12 * sw;
      T.position.set(e.x, y, e.z);
      T.rotation.set(0, e.yaw + Math.PI, 0);
      for (const [o, r] of K.rest) { o.position.copy(r.p); o.rotation.copy(r.r); }
      const tell = e.st === 1, exec = e.st === 2, spent = e.st === 3, stun = e.st === 4;
      const shiver = tell ? Math.sin(time * 38 + e.id) * 0.04 : 0;
      if (N.body) {
        // tell: crouch + shiver; execute: lunge; recover: slumped and spent (G3)
        N.body.position.y -= tell ? 0.14 : spent ? 0.1 : 0;
        N.body.rotation.x = tell ? -0.16 + shiver : exec ? 0.22 : spent ? 0.2 : stun ? Math.sin(time * 20) * 0.08 : 0;
        N.body.rotation.z = shiver;
      }
      const head = N.head ?? N.turret;
      if (head) head.rotation.x = spent ? 0.3 : tell ? -0.12 : 0;
      for (const leg of ['leg_L', 'leg_R', 'leg_B'] as const) {
        const L = N[leg];
        if (!L) continue;
        const ph = leg === 'leg_R' ? Math.PI : leg === 'leg_B' ? Math.PI / 2 : 0;
        L.rotation.x = Math.sin(u.walk + ph) * 0.6 * sw + (tell ? -0.3 : 0);
      }
      for (const arm of ['arm_L', 'arm_R'] as const) {
        const A = N[arm];
        if (!A) continue;
        const sg = arm === 'arm_L' ? 1 : -1;
        A.rotation.x = tell ? -1.1 : exec ? 0.5 : spent ? 0.35 : Math.sin(u.walk + (sg > 0 ? 0 : Math.PI)) * 0.25 * sw;
        A.rotation.z = tell ? sg * 0.25 : 0;
      }
      if (pc === Pc.King) {
        const scut = (net.phase & CourtBit.Scuttle) !== 0;
        // the porthole shutter flaps wider as he bolts; the crown wobbles when he whips
        if (N.back_hatch) N.back_hatch.rotation.x += scut ? 0.25 + Math.sin(time * 16) * 0.12 : 0;
        if (N.crown) N.crown.rotation.z = exec ? Math.sin(time * 30) * 0.12 : 0;
        if (N.base) N.base.rotation.y = u.walk * 0.4;
      }
      T.updateMatrixWorld(true);
      const slot = K.n++;
      for (const p of K.parts) p.inst.setMatrixAt(slot, p.src.matrixWorld);
      // tint: hit flash, burning, primed, the tell's flare
      const h = f.hitAge(e.id);
      this.col.setRGB(1, 1, 1);
      if (e.flags & EFlag.Burn) this.col.setRGB(1.25, 0.82, 0.6);
      if (e.flags & EFlag.Primed) { const k = 0.5 + 0.5 * Math.sin(time * 10); this.col.setRGB(1 + 0.5 * k, 0.75, 1 + 0.6 * k); }
      if (tell) this.col.multiplyScalar(1.12 + 0.12 * Math.sin(time * 22));
      if (h < 0.12) this.col.multiplyScalar(1 + (1 - h / 0.12) * 1.6);
      for (const p of K.parts) p.inst.setColorAt(slot, this.col);
      // G7: the King's gold core throbs (flares when his back is turned and running)
      if (pc === Pc.King) {
        const core = N.wp_back_core;
        if (core) this.v1.setFromMatrixPosition(core.matrixWorld); else this.v1.set(e.x, y + 2.95, e.z);
        // sit the glow just proud of the porthole lens, on his back
        this.v1.x += Math.sin(e.yaw) * 0.25; this.v1.z += Math.cos(e.yaw) * 0.25;
        this.glow.position.copy(this.v1);
        const scut = (net.phase & CourtBit.Scuttle) !== 0;
        (this.glow.material as THREE.SpriteMaterial).opacity = scut ? 0.85 + 0.15 * Math.sin(time * 20) : 0.5 + 0.3 * Math.sin(time * 5);
        this.glow.scale.setScalar(scut ? 2.2 : 1.4);
        coreSeen = e.st !== 5;
      }
    }
    this.glow.visible = coreSeen;
    for (const k of this.kinds) {
      if (!k) continue;
      for (const p of k.parts) {
        p.inst.count = k.n;
        p.inst.instanceMatrix.needsUpdate = true;
        if (p.inst.instanceColor) p.inst.instanceColor.needsUpdate = true;
      }
    }
    if (this.units.size > 40) for (const [id, u] of this.units) if (time - u.seen > 1) this.units.delete(id);
  }

  private actorOf(f: BossFrame, net: CourtNet) {
    for (let i = 0; i < f.n; i++) if (f.units[i].id === net.actor) return f.units[i];
    return null;
  }

  private telegraphs(f: BossFrame, net: CourtNet, time: number) {
    if (!net.stage || !net.atk) return;
    const a = this.actorOf(f, net);
    const tell = net.stage === Stage.Tell, exec = net.stage === Stage.Exec;
    if (!tell && !exec) return;
    const k = tell ? net.prog : 1;
    switch (net.atk as Atk) {
      case Atk.Charge: {
        const dx = Math.sin(net.ldir), dz = Math.cos(net.ldir), len = Math.max(2, net.llen + 1.5);
        const mx = net.lx + dx * len * 0.5, mz = net.lz + dz * len * 0.5;
        this.lane(0, mx, mz, net.ldir, 3.0, len, f.ground(mx, mz), k);
        break;
      }
      case Atk.Mitre: case Atk.Decree: {
        const queen = net.atk === Atk.Decree;
        const len = queen ? 17 : 16, w = queen ? 2.4 : 2.0;
        const ox = a ? a.x : net.lx, oz = a ? a.z : net.lz;
        let n = 0;
        for (let d = queen ? 0 : 4; d < 8; d++) {
          boardDir(net, DIRS8[d][0], DIRS8[d][1], this.o2);
          const mx = net.lx + this.o2.x * (len * 0.5 + 0.6), mz = net.lz + this.o2.z * (len * 0.5 + 0.6);
          const yaw = Math.atan2(this.o2.x, this.o2.z), gy = f.ground(mx, mz);
          this.lane(n, mx, mz, yaw, w, len, gy, k);
          if (exec) {
            // the lines burn: a low wall of light along each
            const b = this.beams[n];
            b.visible = true;
            b.position.set(mx, gy + 0.9, mz);
            b.rotation.set(0, yaw, 0);
            b.scale.set(w * 0.45, 1.8 * (1 - net.prog * 0.6), len);
          }
          n++;
        }
        void ox; void oz;
        break;
      }
      case Atk.Leap: case Atk.Spin: {
        const r = net.atk === Atk.Leap ? 3.0 : 4.0;
        const x = net.atk === Atk.Spin && a ? a.x : net.lx, z = net.atk === Atk.Spin && a ? a.z : net.lz;
        this.strike.mesh.visible = true;
        this.strike.mesh.position.set(x, f.ground(x, z) + TILE_UP + 0.05, z);
        this.strike.mesh.scale.set(r * 2.05, 1, r * 2.05);
        this.strike.u.uK.value = tell ? k : 1;
        this.underDisc.visible = true;
        this.underDisc.position.set(x, this.strike.mesh.position.y - 0.01, z);
        this.underDisc.scale.set(r * 2.05, 1, r * 2.05);
        break;
      }
    }
    void time;
  }

  private lane(i: number, x: number, z: number, yaw: number, w: number, len: number, gy: number, k: number) {
    const l = this.lanes[i];
    l.mesh.visible = true;
    l.mesh.position.set(x, gy + TILE_UP + 0.05, z);
    l.mesh.rotation.set(0, yaw, 0);
    l.mesh.scale.set(w, 1, len);
    l.u.uK.value = k;
    const u = this.under[i];
    u.visible = true;
    u.position.set(x, gy + TILE_UP + 0.04, z);
    u.rotation.set(0, yaw, 0);
    u.scale.set(w * 0.86, 1, len * 0.96);
  }

  bodyDied(type: number, x: number, y: number, z: number) {
    this.ctx.fx()?.robotDeath(this.v1.set(x, y, z), type);
    const king = pieceOf(type) === Pc.King;
    this.ctx.sfx.play(king ? 'kill_big' : 'robot_die', { x, y: y + 1, z }, { pitch: king ? 0.7 : 0.9 });
    this.ctx.sfx.play('debris_clatter', { x, y, z });
    if (king) { this.ctx.shake(0.8); this.ctx.flash(0.6); this.ctx.rumble(1, 0.8, 500); }
  }

  onEvent(kind: number, a: number, _b: number, eid: number, f: BossFrame | null) {
    const sfx = this.ctx.sfx, fx = this.ctx.fx();
    const net = f?.net as CourtNet | undefined;
    const who = f ? f.units.slice(0, f.n).find((u) => u.id === eid) : undefined;
    const pos = who ? { x: who.x, y: who.y + 1.5, z: who.z } : net ? { x: net.bx, y: f!.ground(net.bx, net.bz) + 2, z: net.bz } : undefined;
    switch (kind) {
      case BossBeat.Intro:
        if (a === 1) { sfx.play('ground_pound', pos, { pitch: 0.6 }); sfx.play('boss_phase', undefined, { volume: 0.8 }); this.ctx.shake(0.4); this.ctx.rumble(0.7, 0.5, 300); }
        break;
      case CourtEv.Tock:
        // the court clock: a low wooden tock, the whole army steps together
        sfx.play('melee_hit', pos, { pitch: 0.42, volume: 0.55 });
        break;
      case BossBeat.Attack:
        if (a === Atk.Decree) { sfx.play('service_klaxon', pos, { pitch: 0.7, volume: 0.9 }); this.ctx.rumble(0.5, 0.4, 300); }
        else if (a === Atk.Spin) sfx.play('brute_tell', pos, { pitch: 1.2 });
        else if (a === Atk.Charge) sfx.play('brute_charge', pos, { pitch: 0.9 });
        else if (a === Atk.Leap) sfx.play('jump_push', pos, { pitch: 0.7, volume: 1 });
        else sfx.play('sniper_tell', pos, { pitch: 0.8 });
        break;
      case CourtEv.Impact:
        if (a === Atk.Leap) { sfx.play('explosion', pos, { pitch: 0.8 }); if (who) fx?.dust(this.v1.set(who.x, who.y, who.z), 1); this.ctx.shake(0.35); }
        else if (a === Atk.Spin) { sfx.play('melee_swing', pos, { pitch: 0.6 }); sfx.play('ground_pound', pos, { pitch: 1.3, volume: 0.6 }); }
        else { sfx.play('explosion', pos, { pitch: a === Atk.Decree ? 0.55 : 0.9 }); this.ctx.shake(a === Atk.Decree ? 0.5 : 0.25); this.ctx.chroma(0.3); }
        break;
      case CourtEv.Scuttle: sfx.play('hatch_open', pos, { pitch: 0.8 }); break;
      case CourtEv.Reform: sfx.play('spawn_in', pos, { pitch: 0.8 }); break;
      case CourtEv.Swap: sfx.play('pack_clamp', pos, { pitch: 0.7, volume: 0.6 }); break;
      case CourtEv.Mate:
        sfx.play('boss_phase', undefined, { pitch: 0.8 }); this.ctx.flash(0.7); this.ctx.chroma(0.6); this.burnT = this.time;
        break;
      case BossBeat.Dead:
        sfx.play('explosion', pos, { pitch: 0.5 }); this.ctx.shake(0.6);
        break;
    }
  }

  ray(f: BossFrame, ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxT: number, ap: number, out: BossAim) {
    out.t = -1; out.kind = 0; out.head = false; out.label = '';
    let best = maxT, bv: { part: CPart; av: number; weak: boolean } | null = null, bpc = Pc.Pawn;
    for (let i = 0; i < f.n; i++) {
      const e = f.units[i];
      if (e.st === 5 || !isCourt(e.type)) continue;
      const pc = pieceOf(e.type);
      pieceRaycast(pc, e.x, e.y, e.z, e.yaw, ox, oy, oz, dx, dy, dz, best, this.part as never);
      if (this.part.t >= 0 && this.part.vol) { best = this.part.t; bv = this.part.vol; bpc = pc; out.t = best; }
    }
    if (out.t < 0 || !bv) return out;
    out.kind = bv.weak ? 3 : bv.av > ap ? 4 : 2;
    out.head = bv.weak;
    out.label = `${PIECE_NAMES[bpc]} ${PART_LABEL[bv.part] ?? ''}${bv.av > 0 && bv.part !== CPart.Chassis && bv.part !== CPart.Crown ? ` · AV${bv.av}` : ''}`;
    return out;
  }

  hud(f: BossFrame, aimLabel: string, o: HudBoss) {
    const net = f.net as CourtNet;
    if (net.phase & CourtBit.Intro) return null;
    o.name = this.title; o.sub = 'CHECKMATE THE KING';
    o.hp = f.primary ? f.primary.hp : 0;
    o.count = `${net.alive} OF 16 STANDING`;
    o.pips.length = 0;
    o.hyper = false; o.meter = ''; o.crack = 0;
    o.note = net.stage === Stage.Tell || net.stage === Stage.Exec ? ATTACK_NAMES[net.atk] : net.phase & CourtBit.Scuttle ? 'THE KING RUNS' : '';
    o.warn = net.atk === Atk.Decree && net.stage === Stage.Tell ? 'STAND BETWEEN HER LINES' : '';
    o.aimPart = aimLabel;
    return o;
  }

  objective(f: BossFrame | null, o: HudObjective) {
    const net = f ? (f.net as CourtNet) : null;
    o.title = 'CHECKMATE THE KING';
    if (!net) { o.detail = 'FLANK HIM: HIS BACK IS THE ONLY WAY IN'; return; }
    if (net.phase & CourtBit.Mate) { o.title = 'CHECKMATE'; o.detail = 'THE COURT FALLS WITH ITS KING'; o.tone = 1; return; }
    if (net.stage === Stage.Tell || net.stage === Stage.Exec) {
      switch (net.atk as Atk) {
        case Atk.Decree: o.title = 'ROYAL DECREE'; o.detail = 'STAND BETWEEN THE QUEEN\'S LINES'; o.tone = 2; return;
        case Atk.Charge: o.title = 'ROOK CHARGE'; o.detail = 'GET OFF ITS FILE'; o.tone = 2; return;
        case Atk.Mitre: o.title = 'MITRE BEAMS'; o.detail = 'GET OFF THE BISHOP\'S DIAGONALS'; o.tone = 2; return;
        case Atk.Leap: o.title = 'KNIGHT LEAP'; o.detail = 'GET OUT OF THE RING'; o.tone = 2; return;
        case Atk.Spin: o.title = 'SPIN STRIKE'; o.detail = 'BACK OFF, THEN HIT HIS BACK'; o.tone = 2; return;
      }
    }
    if (net.phase & CourtBit.Scuttle) { o.detail = 'HE\'S RUNNING: SHOOT THE GOLD CORE ON HIS BACK'; o.tone = 1; return; }
    if (net.atk === Atk.Spin && net.stage === Stage.Recover) { o.detail = 'HIS BACK IS TURNED: SHOOT THE GOLD CORE'; o.tone = 1; return; }
    o.detail = 'FLANK HIM: HIS BACK IS THE ONLY WAY IN. FALLEN PIECES RE-FORM';
  }

  dispose() {
    for (const k of this.kinds) if (k) for (const p of k.parts) { this.ctx.scene.remove(p.inst); p.inst.dispose(); }
    this.ctx.scene.remove(this.tiles, this.glow);
    this.tiles.dispose();
    for (const b of this.beams) this.ctx.scene.remove(b);
    for (const u of this.under) this.ctx.scene.remove(u);
    this.ctx.scene.remove(this.underDisc);
    for (const l of this.lanes) l.dispose();
    for (const r of this.reform) r.dispose();
    this.strike.dispose();
    for (const d of this.disposables) d.dispose();
  }
}

function glowTex() {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const g = c.getContext('2d')!;
  const gr = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  gr.addColorStop(0, 'rgba(255,255,255,1)'); gr.addColorStop(0.3, 'rgba(255,255,255,0.5)'); gr.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = gr; g.fillRect(0, 0, 64, 64);
  return new THREE.CanvasTexture(c);
}

export const courtClient: BossClientFactory = (ctx) => new CourtClient(ctx);
